import { createSelector, createSlice } from '@reduxjs/toolkit';
import {
    ArchivedProjectState,
    ProjectState,
    TaskStateObject,
} from '../core/model';
import { RootState } from './index';
import { projectsReducers } from './reducers/projectsReducers';
import { tasksReducers } from './reducers/tasksReducers';
import { workspaceReducers } from './reducers/workspaceReducers';

export type StatusType = 'IDLE' | 'LOADING' | 'SUCCEEDED' | 'FAILED';
export interface WorkspaceState {
    id: string;
    version: number | null;
    projects: ProjectState[];
    archivedProjectsCount: number;
    archivedProjects: {
        projects: ArchivedProjectState[];
        status: StatusType;
        error: string | null;
    };
    tasks: TaskStateObject[];
    plannedTasks: string[];
    automations: any[];
    status: {
        [key: string]: StatusType;
    };
    access: {
        archives: boolean,
        projectSharing: boolean,
        projectsLimit: number,
        automationsLimit: number
    },
    error: {
        [key: string]: string | null;
    };
}

const initialState: WorkspaceState = {
    id: '',
    version: null,
    projects: [],
    archivedProjectsCount: 0,
    archivedProjects: {
        projects: [],
        status: 'IDLE',
        error: null,
    },
    tasks: [],
    plannedTasks: [],
    automations: [],
    access: {
        archives: false,
        projectSharing: false,
        projectsLimit: 5,
        automationsLimit: 3
    },
    status: {
        fetchWorkspace: 'IDLE',
        fetchProjectTasks: 'IDLE',
        fetchArchivedProjects: 'IDLE',
        addProject: 'IDLE',
        renameProject: 'IDLE',
        moveProject: 'IDLE',
        archiveProject: 'IDLE',
        unarchiveProject: 'IDLE',
        deleteProject: 'IDLE',
        addTask: 'IDLE',
        deleteTask: 'IDLE',
        taskAction: 'IDLE',
        plannedTaskAction: 'IDLE',
        hideCompletedTasksAction: 'IDLE',
        idleAllPlannedTasksAction: 'IDLE',
    },
    error: {
        fetchWorkspace: null,
        fetchProjectTasks: null,
        fetchArchivedProjects: null,
        addProject: null,
        renameProject: null,
        moveProject: null,
        archiveProject: null,
        unarchiveProject: null,
        deleteProject: null,
        addTask: null,
        deleteTask: null,
        taskAction: null,
        plannedTaskAction: null,
        hideCompletedTasksAction: null,
        idleAllPlannedTasksAction: null,
    },
};

const workspaceSlice = createSlice({
    name: 'workspace',
    initialState,
    reducers: {
        updateStorePlannedTasksList: (state, action) => {
            state.plannedTasks = action.payload;
        },
    },
    extraReducers: (builder) => {
        workspaceReducers(builder);
        projectsReducers(builder);
        tasksReducers(builder);
    },
});

export const selectWorkspaceData = (state: RootState) => {
    return state.workspace;
};

export const selectProject = (state: RootState, projectId: string) => {
    return state.workspace.projects.filter(
        (project: ProjectState) => project.id === projectId
    )[0];
};

export const selectArchivedProject = (state: RootState, projectId: string) => {
    return state.workspace.archivedProjects.projects.filter(
        (project: any) => project.id === projectId
    )[0];
};

export const selectTask = createSelector(
    [
        (state: RootState, taskId: string | undefined) => {
            const mergedTasks = state.workspace.projects.flatMap((project) =>
                project.tasks.map((task) => ({
                    ...task,
                    projectRef: {
                        id: project.id,
                        name: project.name,
                        color: project.color,
                        workspaceId: project.workspaceId,
                    },
                }))
            );
            const automationsTasks = state.workspace.automations.map(
                (task) => ({
                    ...task,
                    automationId: task.id,
                    projectRef: {
                        id: state.workspace.id,
                        name: 'Automation',
                        color: '#c6c6c6',
                        workspaceId: state.workspace.id,
                    },
                })
            );
            const otherTasks = state.workspace.tasks.map((task) => ({
                ...task,
                projectRef: {
                    id: state.workspace.id,
                    color: '#c6c6c6',
                    workspaceId: state.workspace.id,
                },
            }));
            const allTasks = [
                ...mergedTasks,
                ...automationsTasks,
                ...otherTasks,
            ];

            return allTasks.filter((task: any) => task.id === taskId)[0];
        },
    ],
    (task: any) => task
);

export const { updateStorePlannedTasksList } = workspaceSlice.actions;

export default workspaceSlice.reducer;
